import React, { useEffect, useState } from 'react';
import { 
    Drawer, 
    Box, 
    Typography, 
    IconButton, 
    List, 
    ListItem, 
    ListItemText, 
    Divider, 
    CircularProgress, 
    Button
} from '@mui/material';
import { Close, NotificationsNone } from '@mui/icons-material';
import api from '../utilities/axios';
import { useTheme } from '../styles/theme';

export default function NotificationDrawer({ open, onClose }) {
    const { mode } = useTheme();
    const [notifications, setNotifications] = useState([]);
    const [loading, setLoading] = useState(false);
    const [loadingMore, setLoadingMore] = useState(false);
    const [page, setPage] = useState(1);
    const [hasMore, setHasMore] = useState(false);

    useEffect(() => {
        if (open) {
            setNotifications([]);
            setPage(1);
            fetchNotifications(1);
        }
    }, [open]);

    const fetchNotifications = async (pageNum, append = false) => {
        if (pageNum === 1) setLoading(true);
        else setLoadingMore(true);

        try {
            const res = await api.get(`/user/notifications?page=${pageNum}&pageSize=10&sortBy=date&direction=-1`);
            if (res.data.success) {
                const newNotifications = res.data.notifications || [];
                setNotifications(prev => append ? [...prev, ...newNotifications] : newNotifications);
                
                const pageData = res.data.pageData;
                setHasMore(pageData.currentPage < pageData.totalPages);
            }
        } catch (error) {
            console.error("Failed to fetch notifications", error);
        } finally {
            setLoading(false);
            setLoadingMore(false);
        }
    };

    const handleLoadMore = () => {
        const nextPage = page + 1;
        setPage(nextPage);
        fetchNotifications(nextPage, true);
    };

    return (
        <Drawer
            anchor="right"
            open={open}
            onClose={onClose}
            PaperProps={{
                sx: {
                    width: { xs: '100%', sm: 400 },
                    bgcolor: 'background.paper',
                    backgroundImage: 'none',
                    borderLeft: (theme) => `1px solid ${theme.palette.custom.border}`
                }
            }}
        >
            <Box sx={{ p: 2, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Box display="flex" alignItems="center" gap={1}>
                    <NotificationsNone color="primary" />
                    <Typography variant="h6" fontWeight={600}>Notifications</Typography>
                </Box>
                <IconButton onClick={onClose}>
                    <Close />
                </IconButton>
            </Box>
            <Divider />

            <Box sx={{ flexGrow: 1, overflowY: 'auto', display: 'flex', flexDirection: 'column' }}>
                {loading ? (
                    <Box display="flex" justifyContent="center" p={4}>
                        <CircularProgress size={30} />
                    </Box>
                ) : notifications.length > 0 ? (
                    <>
                        <List sx={{ p: 0 }}>
                            {notifications.map((notification, index) => (
                                <React.Fragment key={notification._id || index}>
                                    <ListItem alignItems="flex-start" sx={{ 
                                        py: 2,
                                        '&:hover': { bgcolor: 'action.hover' }
                                    }}>
                                        <ListItemText
                                            primary={
                                                <Typography variant="subtitle2" fontWeight={600} color="primary">
                                                    {notification.title}
                                                </Typography>
                                            }
                                            secondary={
                                                <Box component="span">
                                                    <Typography variant="body2" color="textPrimary" sx={{ my: 0.5 }}>
                                                        {notification.description}
                                                    </Typography>
                                                    <Typography variant="caption" color="textSecondary">
                                                        {notification.date ? new Date(notification.date).toLocaleString() : ''}
                                                    </Typography>
                                                </Box>
                                            }
                                        />
                                    </ListItem>
                                    <Divider component="li" />
                                </React.Fragment>
                            ))}
                        </List>
                        {hasMore && (
                            <Box sx={{ p: 2, textAlign: 'center' }}>
                                <Button 
                                    size="small" 
                                    onClick={handleLoadMore} 
                                    disabled={loadingMore}
                                    sx={{ textTransform: 'none' }}
                                >
                                    {loadingMore ? <CircularProgress size={20} /> : "Load More"}
                                </Button>
                            </Box>
                        )}
                    </>
                ) : (
                    <Box sx={{ p: 4, textAlign: 'center' }}>
                        <NotificationsNone sx={{ fontSize: 60, color: 'action.disabled', mb: 2 }} />
                        <Typography color="textSecondary">No notifications yet</Typography>
                    </Box>
                )}
            </Box>
        </Drawer>
    );
}
